/**
 * useConversationPartners Hooks
 *
 * React Query hooks for managing conversation partners.
 * Provides CRUD operations with caching and automatic refetching.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createConversationPartner,
  deleteConversationPartner,
  listConversationPartners,
  setPrimaryPartner,
  updateConversationPartner,
} from "../services/conversationPartners";
import type {
  ConversationPartner,
  ConversationPartnerCreate,
  ConversationPartnerListResponse,
  ConversationPartnerUpdate,
  SetPrimaryPartnerResponse,
} from "../types/conversationPartner";

const QUERY_KEY = ["conversationPartners"] as const;

/**
 * Fetch all conversation partners for the current user.
 */
export function useConversationPartners() {
  return useQuery<ConversationPartnerListResponse, Error>({
    queryKey: QUERY_KEY,
    queryFn: listConversationPartners,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get the primary partner from the list (convenience hook).
 */
export function usePrimaryPartner() {
  const { data, ...rest } = useConversationPartners();
  const primaryPartner = data?.partners.find((p) => p.is_primary) ?? null;
  return { data: primaryPartner, ...rest };
}

/**
 * Create a new conversation partner.
 */
export function useCreateConversationPartner() {
  const queryClient = useQueryClient();

  return useMutation<ConversationPartner, Error, ConversationPartnerCreate>({
    mutationFn: createConversationPartner,
    onSuccess: (newPartner) => {
      // Update the cache with the new partner
      queryClient.setQueryData<ConversationPartnerListResponse>(
        QUERY_KEY,
        (old) => {
          if (!old) {
            return { partners: [newPartner], total: 1 };
          }

          // If new partner is primary, demote existing primary
          const partners = newPartner.is_primary
            ? old.partners.map((p) => ({ ...p, is_primary: false }))
            : [...old.partners];

          return {
            partners: [...partners, newPartner],
            total: old.total + 1,
          };
        }
      );
    },
  });
}

/**
 * Update an existing conversation partner.
 */
export function useUpdateConversationPartner() {
  const queryClient = useQueryClient();

  return useMutation<
    ConversationPartner,
    Error,
    { partnerId: string; data: ConversationPartnerUpdate }
  >({
    mutationFn: ({ partnerId, data }) =>
      updateConversationPartner(partnerId, data),
    onSuccess: (updatedPartner) => {
      // Update the cache with the updated partner
      queryClient.setQueryData<ConversationPartnerListResponse>(
        QUERY_KEY,
        (old) => {
          if (!old) return old;

          return {
            partners: old.partners.map((p) =>
              p.id === updatedPartner.id ? updatedPartner : p
            ),
            total: old.total,
          };
        }
      );
    },
  });
}

/**
 * Delete a conversation partner.
 */
export function useDeleteConversationPartner() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: deleteConversationPartner,
    onSuccess: (_, deletedPartnerId) => {
      // Remove from cache
      queryClient.setQueryData<ConversationPartnerListResponse>(
        QUERY_KEY,
        (old) => {
          if (!old) return old;

          return {
            partners: old.partners.filter((p) => p.id !== deletedPartnerId),
            total: old.total - 1,
          };
        }
      );
    },
  });
}

/**
 * Set a partner as the primary partner.
 */
export function useSetPrimaryPartner() {
  const queryClient = useQueryClient();

  return useMutation<SetPrimaryPartnerResponse, Error, string>({
    mutationFn: setPrimaryPartner,
    onSuccess: (response) => {
      // Update the cache: demote all others, promote the new primary
      queryClient.setQueryData<ConversationPartnerListResponse>(
        QUERY_KEY,
        (old) => {
          if (!old) return old;

          return {
            partners: old.partners.map((p) =>
              p.id === response.partner.id
                ? response.partner
                : { ...p, is_primary: false }
            ),
            total: old.total,
          };
        }
      );
    },
  });
}
